import {
  CreateRequestAccessDto,
  DeleteRequestAccessDto,
  RequestAccessDto,
  RequestDto,
  RequestedTaskDto,
} from "./delegation.types";
import delegationService from "./delegationService";
import { CreateTaskAccessForRequestDto, TaskDto } from "../types";
import { createThunkWithError } from "../../featureHelpers/errorParser";

// 1) Create delegation request
export const createDelegationRequest = createThunkWithError<RequestDto, string>(
  "delegation/createRequest",
  async (taskId) => {
    if (!taskId) throw new Error("No task id");
    const request = await delegationService.createRequest({ noteId: taskId });
    return { ...request };
  }
);

// 2) Create request access (with duplicate guard)
export const createRequestAccess = createThunkWithError<
  RequestAccessDto,
  CreateRequestAccessDto
>("delegation/createRequestAccess", async (payload, { getState }) => {
  const { tasks } = getState();
  const existing = tasks.task?.DelegationRequest?.access;

  if (
    existing &&
    existing.some(
      (a) =>
        (payload.groupId && a.groupId === payload.groupId) ||
        (payload.userId && a.userId === payload.userId) ||
        (payload.organizationId && a.organizationId === payload.organizationId)
    )
  ) {
    throw new Error("That access is already requested");
  }

  const hasTarget =
    !!payload.groupId || !!payload.userId || !!payload.organizationId;
  if (!hasTarget) throw new Error("No access path given");
  if (!payload.noteId) throw new Error("No task id");

  const res = await delegationService.createRequestAccess(payload);
  return { ...res };
});
// 3) Delete request access
export const deleteRequestAccess = createThunkWithError<
  string,
  DeleteRequestAccessDto
>("delegation/deleteRequestAccess", async (payload) => {
  if (!payload.noteId) throw new Error("No task id");
  await delegationService.deleteRequestAccess(payload);
  return payload.requestAccessId;
});

// 4) Delete request
export const deleteRequest = createThunkWithError<string, string>(
  "delegation/deleteRequest",
  async (noteId) => {
    if (!noteId) throw new Error("No task id");
    await delegationService.deleteRequest(noteId);
    return noteId;
  }
);

// 5) Get requests
export const getRequests = createThunkWithError<RequestAccessDto[], void>(
  "delegation/getRequests",
  async () => {
    return delegationService.getRequests();
  }
);

// 6) Accept request
export const acceptRequest = createThunkWithError<
  TaskDto,
  { requestId: string; access?: CreateTaskAccessForRequestDto }
>("delegation/acceptRequest", async ({ requestId, access }) => {
  if (!requestId) throw new Error("No request id");
  return delegationService.acceptRequest(requestId, access);
});

// 7) Reject request
export const rejectRequest = createThunkWithError<string, string>(
  "delegation/rejectRequest",
  async (requestId) => {
    if (!requestId) throw new Error("No request id");
    return delegationService.rejectRequest(requestId);
  }
);

// 8) Get requested task
export const getRequestedTask = createThunkWithError<RequestedTaskDto, string>(
  "delegation/getRequestedTask",
  async (requestId) => {
    if (!requestId) throw new Error("No request id");
    return delegationService.getRequestedTask(requestId);
  }
);
